import React, { Component } from 'react';

// Components and Others
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import ModalUtils from 'utils/ModalUtils';
import { remove, clone, findIndex } from 'lodash';

// GraphQL
import DealService from 'services/modules/DealService';
import GraphQLClient from 'services/apollo/GraphQLClient';
import DealsQuery from 'services/graphQL/query/DealsQuery';
import SubscriptionActionType from 'utils/enum/SubscriptionActionType';
import DealSubscription from 'services/graphQL/subscription/DealSubscription';

const EmploymentHistoryContainer = (WrappedComponent) => class extends Component {
    static propTypes = {
        accountNumber: PropTypes.number.isRequired,
        customerId: PropTypes.string.isRequired,
    };

    constructor(props) {
        super(props);
        this.graphqlClient = new GraphQLClient();
        this.dealService = new DealService();
        this.employmentHistorySubscription = null;

        this.state = {
            load: false,
            records: [],
        };

        this.initBind();
    }

    getServicesData() {
        const { props: { accountNumber, customerId } } = this;
        const input = {
            accountNumber,
            customerId,
        };

        this.setState({
            load: true,
        });

        this.graphqlClient
            .query(DealsQuery.GET_EMPLOYMENT_HISTORY, input)
            .then((response) => {
                const { data, graphQLErrors } = response;

                if (graphQLErrors) {
                    ModalUtils.errorMessage(graphQLErrors);
                    return;
                }

                if (data?.getCustomerEmploymentHistory) {
                    const { getCustomerEmploymentHistory } = data;

                    this.setState({
                        records: getCustomerEmploymentHistory,
                    });
                }
            })
            .finally(() => {
                this.setState({
                    load: false,
                });
            });
    }

    addToPreviewsList(customerEmploymentId) {
        const { state: { records }, props: { accountNumber, customerId } } = this;
        const backUp = clone(records);
        const input = {
            dealId: accountNumber,
            customerEmploymentId,
            customerId,
        };

        this.dealService.addPreviousEmployment(input)
            .then((response) => {
                const { data, graphQLErrors } = response;

                if (graphQLErrors) {
                    ModalUtils.errorMessage(graphQLErrors);
                    return;
                }

                if (data && data.assignDealPreviousEmployment) {
                    remove(backUp, { customerEmploymentId });

                    this.setState({
                        records: backUp,
                    });
                }
            });
    }

    subscriptionEmploymentHistory() {
        const { customerId, accountNumber } = this.props;
        const input = {
            accountNumber,
            customerId,
        };

        this.graphqlClient.subscribe(this.responseSubscription, DealSubscription.CUSTOMER_EMPLOYMENT_HISTORY, input)
            .then((response) => {
                this.employmentHistorySubscription = response;
            });
    }

    responseSubscription(record) {
        const { data } = record;

        if (data) {
            const { customerEmploymentHistory } = data;

            switch (customerEmploymentHistory?.type) {
            case SubscriptionActionType.CREATED:
                this.addEmploymentHistory(customerEmploymentHistory?.data);
                break;
            case SubscriptionActionType.DELETED:
                this.removeEmploymentHistory(customerEmploymentHistory?.data);
                break;
            default:
            }
        }
    }

    addEmploymentHistory(record) {
        if (record) {
            this.setState((prevState) => ({
                records: update(prevState.records, { $push: [record] }),
            }));
        }
    }

    removeEmploymentHistory(record) {
        const { records } = this.state;
        const index = findIndex(records, { customerEmploymentId: record?.customerEmploymentId });

        if (index > -1) {
            this.setState((prevState) => ({
                records: update(prevState.records, { $splice: [[index, 1]] }),
            }));
        }
    }

    unsubscribeEmploymentHistory() {
        if (this.employmentHistorySubscription) {
            this.employmentHistorySubscription.unsubscribe();
        }
    }

    initBind() {
        this.getServicesData = this.getServicesData.bind(this);
        this.addToPreviewsList = this.addToPreviewsList.bind(this);
        this.responseSubscription = this.responseSubscription.bind(this);
        this.addEmploymentHistory = this.addEmploymentHistory.bind(this);
        this.removeEmploymentHistory = this.removeEmploymentHistory.bind(this);
        this.unsubscribeEmploymentHistory = this.unsubscribeEmploymentHistory.bind(this);
        this.subscriptionEmploymentHistory = this.subscriptionEmploymentHistory.bind(this);
    }

    render() {
        const { props, state } = this;

        return (
            <WrappedComponent
                {...props}
                {...state}
                getServicesData={this.getServicesData}
                addToPreviewsList={this.addToPreviewsList}
                unsubscribeEmploymentHistory={this.unsubscribeEmploymentHistory}
                subscriptionEmploymentHistory={this.subscriptionEmploymentHistory}
            />
        );
    }
};

export default EmploymentHistoryContainer;
